import React from "react";
import { CircleCheckBig, MapPin } from "lucide-react";

// Sourcing-owned page 1 — independent of ConvAI's JobDescriptionViewer (that file is shared
// with the real ATS apply flow and must stay untouched). Driven purely by the `job` object
// GET /public/screening/{token} returns (read straight from Mongo via the enrollment's job_id
// — no ConvAI cross-call, no dependency on a "shareable link").

const BulletList = ({ items }) => (
    <div className="space-y-2">
        {items.map((item, i) => (
            <div key={i} className="flex items-start gap-3">
                <CircleCheckBig className="w-[17px] h-[17px] flex-shrink-0 mt-[3px] text-[#1569c4]" />
                <span className="text-gray-600 text-[14.5px] leading-[1.8]">{item}</span>
            </div>
        ))}
    </div>
);

const renderDescription = (text) => {
    if (!text) return null;
    const lines = text.split("\n").filter((l) => l.trim() !== "");
    return (
        <div className="space-y-3">
            {lines.map((line, i) => {
                const trimmed = line.trim();
                const isBullet = /^[-•*]\s*/.test(trimmed);
                if (isBullet) {
                    return (
                        <div key={i} className="flex items-start gap-3">
                            <CircleCheckBig className="w-[17px] h-[17px] flex-shrink-0 mt-[3px] text-[#1569c4]" />
                            <span className="text-gray-600 text-[14.5px] leading-[1.8]">
                                {trimmed.replace(/^[-•*]\s*/, "")}
                            </span>
                        </div>
                    );
                }
                return (
                    <p key={i} className="text-gray-600 text-[14.5px] leading-[1.8] whitespace-pre-wrap m-0">
                        {trimmed}
                    </p>
                );
            })}
        </div>
    );
};

const ApplyCard = ({ applyDescription, applyError, applyBusy, onApply, onDecline }) => (
    <div className="rounded-2xl p-6 shadow-sm bg-[#FAFAFA]">
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">{applyDescription}</p>
        {applyError && <p className="text-sm text-red-500 mb-3">{applyError}</p>}
        <button
            onClick={onApply}
            disabled={applyBusy}
            className="w-full py-3 px-6 rounded-xl font-semibold text-sm tracking-wide text-white transition-opacity active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
        >
            I'm interested →
        </button>
        <button
            onClick={onDecline}
            disabled={applyBusy}
            className="w-full mt-3 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
        >
            Not interested
        </button>
    </div>
);

const CandidateJobDetails = ({
    job,
    formName,
    candidateFirstName,
    onApply,
    onDecline,
    applyBusy = false,
    applyError = null,
}) => {
    const applyDescription = candidateFirstName
        ? `Hi ${candidateFirstName}, interested in exploring this opportunity? Answer a few quick questions and our team will be in touch.`
        : "Interested in exploring this opportunity? Answer a few quick questions and our team will be in touch.";

    // No linked job (or it couldn't be resolved from Mongo) — compact intro, same gate.
    if (!job) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="rounded-3xl p-8 shadow-sm bg-white">
                    <h1 className="text-2xl font-semibold text-gray-800">{formName || "A few quick questions"}</h1>
                    {candidateFirstName && <p className="mt-5 text-[15px] text-gray-700">Hi {candidateFirstName} 👋</p>}
                    <div className="mt-8 pt-6 border-t border-gray-100">
                        <p className="text-sm font-medium text-gray-700 mb-4">Interested in this opportunity?</p>
                        {applyError && <p className="text-sm text-red-500 mb-3">{applyError}</p>}
                        <div className="flex items-center gap-5 flex-wrap">
                            <button
                                onClick={onApply}
                                disabled={applyBusy}
                                className="px-8 py-3 rounded-full text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                I'm interested
                            </button>
                            <button
                                onClick={onDecline}
                                disabled={applyBusy}
                                className="text-sm text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                            >
                                Not interested
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const experience =
        job.min_experience === 0 && job.max_experience === 0
            ? "Fresher"
            : `${job.min_experience ?? "--"} – ${job.max_experience ?? "--"} Years`;
    const postedLabel = job.posting_date
        ? new Date(job.posting_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })
        : null;

    return (
        <div className="mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 items-start">
                {/* LEFT — role overview + about company */}
                <div className="flex-1 flex flex-col gap-6 min-w-0">
                    <div className="rounded-2xl p-4 sm:p-8 shadow-sm bg-[#FAFAFA]">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                            Role Overview
                        </p>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">{job.job_role}</h2>
                        {job.company_name && (
                            <p className="text-sm text-gray-500 mb-4">{job.company_name}</p>
                        )}
                        {renderDescription(job.description)}

                        {job.responsibilities?.length > 0 && (
                            <div className="mt-5">
                                <div className="border-t border-gray-200 mb-5" />
                                <h5 className="text-[13.5px] font-semibold text-gray-800 mb-4">Key Responsibilities</h5>
                                <BulletList items={job.responsibilities} />
                            </div>
                        )}

                        {job.skills?.length > 0 && (
                            <div className="mt-5">
                                <div className="border-t border-gray-200 mb-5" />
                                <h5 className="text-[13.5px] font-semibold text-gray-800 mb-4">Required Skills</h5>
                                <BulletList items={job.skills} />
                            </div>
                        )}
                    </div>

                    {job.about_company && (
                        <div className="rounded-2xl p-8 shadow-sm bg-[#FAFAFA]">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                                About The Company
                            </p>
                            {job.company_name && (
                                <p className="font-semibold text-gray-900 mb-4">{job.company_name}</p>
                            )}
                            <div className="border-t border-gray-200 mb-4" />
                            <div className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
                                {job.about_company}
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT — job details + apply gate */}
                <div className="w-full lg:w-80 flex flex-col gap-6 lg:sticky lg:top-8 shrink-0">
                    <div className="rounded-2xl p-6 shadow-sm bg-[#FAFAFA]">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                            Job Details
                        </p>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                                <span className="text-sm text-gray-600">Experience</span>
                                <span className="font-semibold text-sm text-gray-900">{experience}</span>
                            </div>
                            {job.location && (
                                <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                                    <span className="text-sm text-gray-600">Location</span>
                                    <span className="font-semibold text-sm text-gray-900 flex items-center gap-1">
                                        <MapPin className="w-3.5 h-3.5" /> {job.location}
                                    </span>
                                </div>
                            )}
                            {postedLabel && (
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Posted</span>
                                    <span className="font-semibold text-sm text-gray-900">{postedLabel}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <ApplyCard
                        applyDescription={applyDescription}
                        applyError={applyError}
                        applyBusy={applyBusy}
                        onApply={onApply}
                        onDecline={onDecline}
                    />
                </div>
            </div>
        </div>
    );
};

export default CandidateJobDetails;